import { FormEvent, useState } from "react";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import useCohorts from "cohorts/hooks/useCohorts";
import { CohortSummary } from "cohorts/types/interfaces/cohort";
import TextOverflow from "~/components/TextOverflow";
import { trackEvent } from "~/utils/monitoring";

interface ModalProps {
  cohort: CohortSummary;
  open: boolean;
  handleClose: () => void;
}

const ModalBody = ({ cohort, handleClose }: ModalProps) => {
  const { mutate: mutateCohorts } = useCohorts();
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const resp = await fetch(`/api/cohort-explorer/cohorts/${cohort.id}`, {
      method: "DELETE",
    });

    if (resp.ok) {
      await mutateCohorts();

      trackEvent("cohort_deleted", {
        cohort_id: cohort.id,
        dataset_id: cohort.dataset_id,
      });

      handleClose();
    } else {
      setError("Unable to delete cohort");
    }

    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <DialogTitle>
        <TextOverflow>Delete &quot;{cohort.name}&quot;</TextOverflow>
      </DialogTitle>
      <DialogContent sx={{ gap: 2, fontSize: 14 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Box>Are you sure you want to delete this cohort?</Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <LoadingButton
          type="submit"
          variant="contained"
          loading={submitting}
          sx={{
            backgroundColor: "attention.main",
            "&:hover": { backgroundColor: "attention.dark" },
          }}
        >
          Delete
        </LoadingButton>
      </DialogActions>
    </form>
  );
};

const DeleteCohortModal = (props: ModalProps) => (
  <Dialog fullWidth open={props.open} onClose={props.handleClose}>
    <ModalBody {...props} />
  </Dialog>
);

export default DeleteCohortModal;
